<template>
  <div class="rmr-page-shell gp-page">

    <rmr-page-hero
      title="Fiches"
      title-accent="Marque"
      subtitle="Génération PPTX, conversion PDF et publication des présentations marque"
      :icon="icons.mdiTag"
    >
      <template #actions>
        <rmr-icon-button
          :icon="icons.mdiHelpCircleOutline"
          variant="edit"
          size="sm"
          tooltip="Guide d'utilisation"
          @click="helpDialog = true"
        />
        <rmr-action-button
          label="Actualiser"
          :icon="icons.mdiRefresh"
          variant="translucent"
          size="sm"
          :loading="activeView === 'table' && loading"
          :spin-icon="activeView === 'table' && loading"
          @click="activeView === 'table' ? loadBrands() : setActiveView('table')"
        />
      </template>
    </rmr-page-hero>

    <rmr-modal
      v-model="pptxOverrideDialog"
      variant="form"
      :icon="icons.mdiFilePowerpoint"
      eyebrow="Fiche Marque"
      :title="pptxOverrideBrand ? pptxOverrideBrand.brand_name : ''"
      subtitle="Le PPTX manuel sert de source au PDF tant qu'il est plus récent que le PPTX généré."
      max-width="640"
      @close="closePptxOverrideDialog"
    >
      <template #body>
        <v-card-text class="pa-6">
          <div
            v-if="hasAnyPptxPriorityWarning"
            class="pdf-override-priority-warning mb-5"
          >
            <div class="pdf-override-priority-warning__title">Priorité de publication</div>
            <div class="pdf-override-priority-warning__text">
              Un PDF manuel existe déjà pour au moins une langue. Il sera publié tant qu'il est plus récent
              que le PDF généré.
            </div>
          </div>

          <div
            class="pdf-override-section mb-5"
            :class="{ 'pdf-override-section--active': pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_fr }"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="pdf-override-section__title">
                <span class="pdf-override-section__label">Version francaise</span>
                <rmr-status-chip
                  v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_fr"
                  label="Déposée"
                  variant="success"
                  size="sm"
                />
              </div>
              <div class="pdf-override-section__status">
                <rmr-icon-button
                  v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_fr"
                  :icon="icons.mdiDownload"
                  variant="edit"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Télécharger la version manuelle PPTX française"
                  @click="downloadOverridePptx('fr')"
                />
                <rmr-icon-button
                  v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_fr"
                  :icon="icons.mdiTrashCan"
                  variant="delete"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Supprimer la version manuelle PPTX française"
                  :loading="deletingOverrideType === 'pptx-fr'"
                  @click="deletePptxOverrideFromDialog('fr')"
                />
                <span
                  v-if="pptxOverrideBrand && !pptxOverrideBrand.has_pptx_override_fr"
                  class="pdf-override-section__hint"
                >
                  Aucune version manuelle
                </span>
              </div>
            </div>
            <div class="pdf-override-section__inputs">
              <rmr-file-input
                v-model="overridePptxFileFr"
                accept=".pptx"
                label="PPTX français"
                placeholder="Sélectionner un fichier PPTX"
                hide-details
                :icon="icons.mdiFilePowerpoint"
                clearable
              />
            </div>
            <div
              v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_fr && pptxOverrideBrand.has_pdf_override_fr"
              class="pdf-override-section__priority-note"
            >
              Un PDF manuel existe déjà pour cette langue. Si vous générez un nouveau PDF depuis ce PPTX, il remplacera la version PDF manuelle actuelle.
            </div>
          </div>

          <div
            class="pdf-override-section"
            :class="{ 'pdf-override-section--active': pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_en }"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="pdf-override-section__title">
                <span class="pdf-override-section__label">Version anglaise</span>
                <rmr-status-chip
                  v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_en"
                  label="Déposée"
                  variant="success"
                  size="sm"
                />
              </div>
              <div class="pdf-override-section__status">
                <rmr-icon-button
                  v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_en"
                  :icon="icons.mdiDownload"
                  variant="edit"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Télécharger la version manuelle PPTX anglaise"
                  @click="downloadOverridePptx('en')"
                />
                <rmr-icon-button
                  v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_en"
                  :icon="icons.mdiTrashCan"
                  variant="delete"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Supprimer la version manuelle PPTX anglaise"
                  :loading="deletingOverrideType === 'pptx-en'"
                  @click="deletePptxOverrideFromDialog('en')"
                />
                <span
                  v-if="pptxOverrideBrand && !pptxOverrideBrand.has_pptx_override_en"
                  class="pdf-override-section__hint"
                >
                  Aucune version manuelle
                </span>
              </div>
            </div>
            <div class="pdf-override-section__inputs">
              <rmr-file-input
                v-model="overridePptxFileEn"
                accept=".pptx"
                label="PPTX anglais"
                placeholder="Sélectionner un fichier PPTX"
                hide-details
                :icon="icons.mdiFilePowerpoint"
                clearable
              />
            </div>
            <div
              v-if="pptxOverrideBrand && pptxOverrideBrand.has_pptx_override_en && pptxOverrideBrand.has_pdf_override_en"
              class="pdf-override-section__priority-note"
            >
              Un PDF manuel existe déjà pour cette langue. Si vous générez un nouveau PDF depuis ce PPTX, il remplacera la version PDF manuelle actuelle.
            </div>
          </div>
        </v-card-text>
      </template>
      <template #footer>
        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <rmr-action-button label="Annuler" variant="ghost" @click="closePptxOverrideDialog" />
          <rmr-action-button
            label="Uploader"
            variant="primary"
            :icon="icons.mdiUpload"
            :disabled="!hasAnyPptxOverrideUploadPending"
            :loading="uploadingOverrideBrandId !== null"
            @click="confirmPptxOverrideUpload"
          />
        </v-card-actions>
      </template>
    </rmr-modal>

    <rmr-modal
      v-model="pdfOverrideDialog"
      variant="form"
      :icon="icons.mdiFilePdfBox"
      eyebrow="Fiche Marque"
      :title="pdfOverrideBrand ? pdfOverrideBrand.brand_name : ''"
      subtitle="Le PDF manuel est publié tant qu'il est plus récent que le PDF généré."
      max-width="640"
      @close="closePdfOverrideDialog"
    >
      <template #body>
        <v-card-text class="pa-6">
          <div
            class="pdf-override-section mb-5"
            :class="{ 'pdf-override-section--active': pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_fr }"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="pdf-override-section__title">
                <span class="pdf-override-section__label">Version francaise</span>
                <rmr-status-chip
                  v-if="pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_fr"
                  label="Déposée"
                  variant="success"
                  size="sm"
                />
              </div>
              <div class="pdf-override-section__status">
                <rmr-icon-button
                  v-if="pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_fr"
                  :icon="icons.mdiEye"
                  variant="edit"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Ouvrir la version manuelle PDF française"
                  @click="openOverridePdf('fr')"
                />
                <rmr-icon-button
                  v-if="pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_fr"
                  :icon="icons.mdiTrashCan"
                  variant="delete"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Supprimer la version manuelle PDF française"
                  :loading="deletingOverrideType === 'pdf-fr'"
                  @click="deletePdfOverrideFromDialog('fr')"
                />
                <span
                  v-if="pdfOverrideBrand && !pdfOverrideBrand.has_pdf_override_fr"
                  class="pdf-override-section__hint"
                >
                  Aucune version manuelle
                </span>
              </div>
            </div>
            <div class="pdf-override-section__inputs">
              <rmr-file-input
                v-model="overridePdfFileFr"
                accept=".pdf"
                label="PDF français"
                placeholder="Sélectionner un fichier PDF"
                hide-details
                :icon="icons.mdiFilePdfBox"
                clearable
              />
            </div>
          </div>

          <div
            class="pdf-override-section"
            :class="{ 'pdf-override-section--active': pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_en }"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="pdf-override-section__title">
                <span class="pdf-override-section__label">Version anglaise</span>
                <rmr-status-chip
                  v-if="pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_en"
                  label="Déposée"
                  variant="success"
                  size="sm"
                />
              </div>
              <div class="pdf-override-section__status">
                <rmr-icon-button
                  v-if="pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_en"
                  :icon="icons.mdiEye"
                  variant="edit"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Ouvrir la version manuelle PDF anglaise"
                  @click="openOverridePdf('en')"
                />
                <rmr-icon-button
                  v-if="pdfOverrideBrand && pdfOverrideBrand.has_pdf_override_en"
                  :icon="icons.mdiTrashCan"
                  variant="delete"
                  size="xs"
                  class="pdf-override-section__remove"
                  tooltip="Supprimer la version manuelle PDF anglaise"
                  :loading="deletingOverrideType === 'pdf-en'"
                  @click="deletePdfOverrideFromDialog('en')"
                />
                <span
                  v-if="pdfOverrideBrand && !pdfOverrideBrand.has_pdf_override_en"
                  class="pdf-override-section__hint"
                >
                  Aucune version manuelle
                </span>
              </div>
            </div>
            <div class="pdf-override-section__inputs">
              <rmr-file-input
                v-model="overridePdfFileEn"
                accept=".pdf"
                label="PDF anglais"
                placeholder="Sélectionner un fichier PDF"
                hide-details
                :icon="icons.mdiFilePdfBox"
                clearable
              />
            </div>
          </div>
        </v-card-text>
      </template>
      <template #footer>
        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <rmr-action-button label="Annuler" variant="ghost" @click="closePdfOverrideDialog" />
          <rmr-action-button
            label="Uploader"
            variant="primary"
            :icon="icons.mdiUpload"
            :disabled="!hasAnyPdfOverrideUploadPending"
            :loading="uploadingOverrideBrandId !== null"
            @click="confirmPdfOverrideUpload"
          />
        </v-card-actions>
      </template>
    </rmr-modal>

    <rmr-modal
      v-model="helpDialog"
      variant="form"
      :icon="icons.mdiHelpCircleOutline"
      eyebrow="Guide"
      title="Générer, corriger et publier"
      subtitle="Flux recommandé pour générer, corriger et publier une fiche marque."
      max-width="680"
    >
      <template #body>
        <v-card-text class="pa-6 gp-help">
          <div class="gp-help__section">
            <div class="gp-help__title">Workflow standard</div>
            <ol class="gp-help__list">
              <li>Ouvrez `Options PPT`, puis générez la présentation PPT.</li>
              <li>Ouvrez `Options PDF`, puis générez le PDF si le rendu est correct.</li>
              <li>Publiez la version PDF disponible.</li>
            </ol>
          </div>

          <div class="gp-help__section">
            <div class="gp-help__title">Corriger avec un PPTX manuel</div>
            <ol class="gp-help__list">
              <li>Ouvrez `Options PPT`, puis uploadez un PPTX manuel corrigé.</li>
              <li>Ouvrez `Options PDF`, puis regénérez le PDF à partir de ce PPTX manuel.</li>
              <li>Vérifiez ensuite la version PDF finale avant publication.</li>
            </ol>
          </div>

          <div class="gp-help__section">
            <div class="gp-help__title">Règles de priorité</div>
            <ul class="gp-help__list">
              <li>Le PDF est généré à partir du PPTX le plus récent, manuel ou généré.</li>
              <li>La publication utilise le PDF le plus récent, manuel ou généré.</li>
              <li>Regénérer la présentation après un dépôt manuel rend donc la version manuelle inactive.</li>
              <li>Si vous regénérez un PDF depuis un PPTX manuel, le PDF manuel actuel est remplacé.</li>
            </ul>
          </div>

          <div class="gp-help__section">
            <div class="gp-help__title">Conditions de publication</div>
            <ul class="gp-help__list">
              <li>Une version PDF disponible est requise pour publier.</li>
              <li>Des données rapport sont obligatoires pour publier.</li>
              <li>Sans slug de marque valide, aucune génération ou version manuelle n'est possible.</li>
            </ul>
          </div>
        </v-card-text>
      </template>
    </rmr-modal>

    <section class="rmr-page-shell__content gp-content">
      <transition name="gp-card-slide" mode="out-in">
      <generate-presentations-table-card
        v-if="activeView === 'table'"
        key="table"
        :brands="brands"
        :headers="headers"
        :loading="loading"
        :search="search"
        :generating-ppt-brand-id="generatingPptBrandId"
        :generating-pdf-brand-id="generatingPdfBrandId"
        :publishing-brand-id="publishingBrandId"
        :success-color="colors.success"
        :get-publish-tooltip="getPublishTooltip"
        :get-publish-tooltip-class="getPublishTooltipClass"
        @update:search="search = $event"
        @show-history="setActiveView('history')"
        @generate="generatePresentation"
        @convert-pdf="convertToPdf"
        @open-pptx-override="openPptxOverrideDialog"
        @open-pdf-override="openPdfOverrideDialog"
        @publish="publishPdf"
      />
      <generate-presentations-history-card
        v-else
        key="history"
        @back="setActiveView('table')"
      />
      </transition>
    </section>

  </div>
</template>

<script>
import { formatDateTimeFrench } from "@/utils/dates";
import {
  mdiRefresh,
  mdiTag,
  mdiHelpCircleOutline,
  mdiFilePdfBox,
  mdiFilePowerpoint,
  mdiEye,
  mdiDownload,
  mdiUpload,
  mdiTrashCan,
} from "@mdi/js";
import { generateTemplate1 } from "./pptx-templates/template1";
import { generateTemplate2 } from "./pptx-templates/template2";
import { generateTemplate3 } from "./pptx-templates/template3";
import { generateTemplate4 } from "./pptx-templates/template4";
import GeneratePresentationsHistoryCard from "@/components/marketing/generate-presentations/HistoryCard.vue";
import GeneratePresentationsTableCard from "@/components/marketing/generate-presentations/TableCard.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrFileInput from "@/components/ui/forms/RmrFileInput.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import { RMR_SUCCESS } from "@/styles/tokens";

export default {
  name: "GeneratePresentations",
  components: {
    GeneratePresentationsHistoryCard,
    GeneratePresentationsTableCard,
    RmrPageHero,
    RmrModal,
    RmrActionButton,
    RmrIconButton,
    RmrFileInput,
    RmrStatusChip,
  },
  data() {
    return {
      activeView: "table",
      brands: [],
      sourceSettings: {},
      loading: false,
      search: "",
      helpDialog: false,
      generatingPptBrandId: null,
      generatingPdfBrandId: null,
      publishingBrandId: null,
      pptxOverrideDialog: false,
      pptxOverrideBrand: null,
      pdfOverrideDialog: false,
      pdfOverrideBrand: null,
      overridePdfFileFr: null,
      overridePdfFileEn: null,
      overridePptxFileFr: null,
      overridePptxFileEn: null,
      uploadingOverrideBrandId: null,
      deletingOverrideType: null,
      headers: [
        { text: "Marque",    value: "brand_name", width: "220px" },
        { text: "Template",  value: "template_number", width: "130px" },
        { text: "Images",    value: "images",   sortable: false, width: "250px" },
        { text: "Données",   value: "report",   width: "120px", align: "center" },
        { text: "Actions",   value: "actions",  sortable: false, width: "360px", align: "end" },
      ],
      icons: {
        mdiRefresh,
        mdiTag,
        mdiHelpCircleOutline,
        mdiFilePdfBox,
        mdiFilePowerpoint,
        mdiEye,
        mdiDownload,
        mdiUpload,
        mdiTrashCan,
      },
      colors: {
        success: RMR_SUCCESS,
      },
    };
  },
  mounted() {
    this.loadBrands();
    this.loadSourceSettings();
  },
  computed: {
    hasAnyPptxOverrideUploadPending() {
      return Boolean(
        this.overridePptxFileFr ||
        this.overridePptxFileEn
      );
    },
    hasAnyPdfOverrideUploadPending() {
      return Boolean(
        this.overridePdfFileFr ||
        this.overridePdfFileEn
      );
    },
    hasAnyPptxPriorityWarning() {
      const brand = this.pptxOverrideBrand;
      if (!brand) return false;
      return (
        (brand.has_pptx_override_fr && brand.has_pdf_override_fr) ||
        (brand.has_pptx_override_en && brand.has_pdf_override_en)
      );
    },
  },
  methods: {
    setActiveView(view) {
      this.activeView = view;
    },
    getNormalizedBrandSlug(brand) {
      return typeof brand?.brand_slug === "string" ? brand.brand_slug.trim() : "";
    },
    getOverrideAssetUrl(brand, filename) {
      const brandSlug = this.getNormalizedBrandSlug(brand);
      if (!brandSlug) return "";
      const apiBaseUrl = (localStorage.getItem("apiUrl") ?? process.env.VUE_APP_API_URL ?? "").replace(/\/$/, "");
      return `${apiBaseUrl}/uploads/marketing/presentations/${encodeURIComponent(brandSlug)}/override/${filename}`;
    },
    openOverridePdf(lang) {
      const brand = this.pdfOverrideBrand;
      if (!brand) return;
      const url = this.getOverrideAssetUrl(brand, `${lang}.pdf`);
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    downloadOverridePptx(lang) {
      const brand = this.pptxOverrideBrand;
      if (!brand) return;
      const url = this.getOverrideAssetUrl(brand, `${lang}.pptx`);
      if (!url) return;
      const link = document.createElement("a");
      link.href = url;
      link.download = `${brand.brand_name} ${lang.toUpperCase()} version manuelle.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async loadSourceSettings() {
      try {
        this.sourceSettings = await this.$store.dispatch(
          "marketing/fetchSourceSettings"
        );
      } catch (error) {
        console.error("Error loading source settings:", error);
      }
    },
    async loadBrands() {
      this.loading = true;
      try {
        this.brands = await this.$store.dispatch(
          "marketing/fetchBrandsForGeneration"
        );
      } catch (error) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors du chargement des marques: " + error.message,
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
    async generatePresentation(brand) {
      if (!brand.template_number) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Veuillez définir un numéro de template pour cette marque",
          color: "warning",
          timeout: 5000,
        });
        return;
      }

      const brandSlug = this.getNormalizedBrandSlug(brand);
      if (!brandSlug) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant de générer.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }

      this.generatingPptBrandId = brand.id;

      try {
        const languages = ["fr", "en"];
        const results = [];

        for (const lang of languages) {
          try {
            let pptx;
            switch (brand.template_number) {
              case 1:
                pptx = await generateTemplate1(brand, this.sourceSettings, lang);
                break;
              case 2:
                pptx = await generateTemplate2(brand, this.sourceSettings, lang);
                break;
              case 3:
                pptx = await generateTemplate3(brand, this.sourceSettings, lang);
                break;
              case 4:
                pptx = await generateTemplate4(brand, this.sourceSettings, lang);
                break;
              default:
                throw new Error("Template " + brand.template_number + " non supporté");
            }

            const fileSuffix = lang === "fr" ? " [FR]" : " [EN]";
            const fileName = brand.brand_name + fileSuffix + ".pptx";

            // Download file to user's machine
            await pptx.writeFile({ fileName });

            // Upload to server
            const blob = await pptx.write({ outputType: "blob" });
            const file = new File([blob], fileName, {
              type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            });

            await this.$store.dispatch("marketing/uploadPresentation", {
              file,
              filename: brand.brand_name + fileSuffix,
              brandSlug,
            });
            results.push({ lang, status: "success" });
          } catch (err) {
            console.error(`Error generating PPT ${lang}:`, err);
            results.push({ lang, status: "error", error: err.message });
          }
        }

        const messages = results.map(r => {
          if (r.status === "success") {
            const fileName = brand.brand_name + (r.lang === "fr" ? " [FR]" : " [EN]") + ".pptx";
            return `✅ Présentation ${r.lang.toUpperCase()} générée et sauvegardée: ${fileName}`;
          } else {
            return `❌ Erreur Présentation ${r.lang.toUpperCase()}: ${r.error}`;
          }
        });

        const hasErrors = results.some(r => r.status === "error");

        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: messages.join("<br>"),
          color: hasErrors ? "error" : "success",
          timeout: 6000,
        });

        await this.loadBrands();
      } catch (error) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors de la génération: " + error.message,
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.generatingPptBrandId = null;
      }
    },
    async convertToPdf(brand) {
      const brandSlug = this.getNormalizedBrandSlug(brand);
      if (!brandSlug) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant de générer un PDF.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }

      this.generatingPdfBrandId = brand.id;

      try {
        const languages = ["fr", "en"];
        const results = [];

        for (const lang of languages) {
          const fileSuffix = lang === "fr" ? " [FR]" : " [EN]";
          const fileName = brand.brand_name + fileSuffix;

          try {
            const blob = await this.$store.dispatch("marketing/convertToPdf", {
              brandSlug,
              filename: fileName,
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName + ".pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            results.push({ lang, status: "success" });
          } catch (err) {
            // Only report as error if it's not a 404 (file missing is expected)
            if (err.response && err.response.status === 404) {
              console.log(`PPTX for ${lang} not found, skipping PDF conversion.`);
            } else {
              console.error(`Error converting ${lang}:`, err);
              results.push({ lang, status: "error", error: err.message });
            }
          }
        }

        const messages = results.map(r => {
          if (r.status === "success") {
            const fileName = brand.brand_name + (r.lang === "fr" ? " [FR]" : " [EN]") + ".pdf";
            return `✅ PDF ${r.lang.toUpperCase()} téléchargé: ${fileName}`;
          } else {
            return `❌ Erreur PDF ${r.lang.toUpperCase()}: ${r.error}`;
          }
        });

        if (messages.length === 0) {
          this.$store.dispatch("app/setSnackBar", {
            show: true,
            text: "Aucun fichier PPTX trouvé pour la conversion PDF.",
            color: "info",
            timeout: 5000,
          });
          return;
        }

        const hasErrors = results.some(r => r.status === "error");

        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: messages.join("<br>"),
          color: hasErrors ? "error" : "success",
          timeout: 6000,
        });

        // Reload brands to update has_pdf status
        await this.loadBrands();
      } catch (error) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors de la conversion: " + error.message,
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.generatingPdfBrandId = null;
      }
    },
    async publishPdf(brand) {
      this.publishingBrandId = brand.id;
      try {
        const result = await this.$store.dispatch(
          "marketing/publishBrand",
          brand.id
        );
        const success = Boolean(result && result.success);

        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: success ? "Publication terminée avec succès." : "Erreur lors de la publication.",
          color: success ? "success" : "error",
          timeout: 5000,
        });
      } catch (error) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors de la publication.",
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.publishingBrandId = null;
        // Refresh list so tooltip shows updated last publication metadata.
        await this.loadBrands();
      }
    },
    getPublishTooltip(brand) {
      const p = brand?.last_publication;
      if (!p?.published_at) {
        if (!brand.has_pdf && !brand.has_pdf_override_fr && !brand.has_pdf_override_en) return "Aucun PDF disponible";
        if (!brand.has_report_data) return "Aucune donnée rapport";
        return "Publier sur le site";
      }
      if (p.status && p.status !== "success") {
        return `Echec de la derniere publication — ${p.published_by || "?"} — ${formatDateTimeFrench(p.published_at)}`;
      }
      return `Dernière pub: ${p.published_by || "?"} — ${formatDateTimeFrench(p.published_at)}`;
    },
    getPublishTooltipClass(brand) {
      const hasFailure = brand?.last_publication?.published_at && brand?.last_publication?.status && brand.last_publication.status !== "success";
      return hasFailure ? "gp-publish-tooltip gp-publish-tooltip--error" : "gp-publish-tooltip";
    },
    openPptxOverrideDialog(brand) {
      if (!this.getNormalizedBrandSlug(brand)) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant d'uploader une version manuelle.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }

      this.pptxOverrideBrand = { ...brand };
      this.overridePptxFileFr = null;
      this.overridePptxFileEn = null;
      this.pptxOverrideDialog = true;
    },
    openPdfOverrideDialog(brand) {
      if (!this.getNormalizedBrandSlug(brand)) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant d'uploader une version manuelle.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }

      this.pdfOverrideBrand = { ...brand };
      this.overridePdfFileFr = null;
      this.overridePdfFileEn = null;
      this.pdfOverrideDialog = true;
    },
    closePptxOverrideDialog() {
      this.pptxOverrideDialog = false;
      this.pptxOverrideBrand = null;
      this.overridePptxFileFr = null;
      this.overridePptxFileEn = null;
    },
    closePdfOverrideDialog() {
      this.pdfOverrideDialog = false;
      this.pdfOverrideBrand = null;
      this.overridePdfFileFr = null;
      this.overridePdfFileEn = null;
    },
    async confirmPptxOverrideUpload() {
      if (!this.pptxOverrideBrand) return;
      const brand = this.pptxOverrideBrand;
      const brandSlug = this.getNormalizedBrandSlug(brand);
      if (!brandSlug) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant d'uploader une version manuelle.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }
      this.uploadingOverrideBrandId = brand.id;
      try {
        await this.$store.dispatch("marketing/uploadPptxOverride", {
          brandSlug,
          pptxFr: this.overridePptxFileFr || null,
          pptxEn: this.overridePptxFileEn || null,
        });
        this.$store.dispatch("app/setSnackBar", { show: true, text: "PPTX manuel uploadé.", color: "success", timeout: 4000 });
        this.closePptxOverrideDialog();
        await this.loadBrands();
      } catch (e) {
        this.$store.dispatch("app/setSnackBar", { show: true, text: "Erreur upload: " + e.message, color: "error", timeout: 5000 });
      } finally {
        this.uploadingOverrideBrandId = null;
      }
    },
    async confirmPdfOverrideUpload() {
      if (!this.pdfOverrideBrand) return;
      const brand = this.pdfOverrideBrand;
      const brandSlug = this.getNormalizedBrandSlug(brand);
      if (!brandSlug) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant d'uploader une version manuelle.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }
      this.uploadingOverrideBrandId = brand.id;
      try {
        await this.$store.dispatch("marketing/uploadPdfOverride", {
          brandSlug,
          pdfFr: this.overridePdfFileFr || null,
          pdfEn: this.overridePdfFileEn || null,
        });
        this.$store.dispatch("app/setSnackBar", { show: true, text: "PDF manuel uploadé.", color: "success", timeout: 4000 });
        this.closePdfOverrideDialog();
        await this.loadBrands();
      } catch (e) {
        this.$store.dispatch("app/setSnackBar", { show: true, text: "Erreur upload: " + e.message, color: "error", timeout: 5000 });
      } finally {
        this.uploadingOverrideBrandId = null;
      }
    },
    async deletePptxOverrideFromDialog(lang) {
      if (!this.pptxOverrideBrand) return;
      const brandSlug = this.getNormalizedBrandSlug(this.pptxOverrideBrand);
      if (!brandSlug) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant de supprimer une version manuelle.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }
      this.deletingOverrideType = `pptx-${lang}`;
      try {
        await this.$store.dispatch("marketing/deletePptxOverride", { brandSlug, lang });
        this.$store.dispatch("app/setSnackBar", { show: true, text: `PPTX ${lang.toUpperCase()} manuel supprimé.`, color: "success", timeout: 4000 });
        this.pptxOverrideBrand = {
          ...this.pptxOverrideBrand,
          ...(lang === "fr" ? { has_pptx_override_fr: false } : { has_pptx_override_en: false }),
        };
        await this.loadBrands();
      } catch (e) {
        this.$store.dispatch("app/setSnackBar", { show: true, text: "Erreur suppression: " + e.message, color: "error", timeout: 5000 });
      } finally {
        this.deletingOverrideType = null;
      }
    },
    async deletePdfOverrideFromDialog(lang) {
      if (!this.pdfOverrideBrand) return;
      const brandSlug = this.getNormalizedBrandSlug(this.pdfOverrideBrand);
      if (!brandSlug) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le slug de la marque est manquant. Veuillez le renseigner avant de supprimer une version manuelle.",
          color: "warning",
          timeout: 5000,
        });
        return;
      }
      this.deletingOverrideType = `pdf-${lang}`;
      try {
        await this.$store.dispatch("marketing/deletePdfOverride", { brandSlug, lang });
        this.$store.dispatch("app/setSnackBar", { show: true, text: `PDF ${lang.toUpperCase()} manuel supprimé.`, color: "success", timeout: 4000 });
        this.pdfOverrideBrand = { ...this.pdfOverrideBrand, ...(lang === "fr" ? { has_pdf_override_fr: false } : { has_pdf_override_en: false }) };
        await this.loadBrands();
      } catch (e) {
        this.$store.dispatch("app/setSnackBar", { show: true, text: "Erreur suppression: " + e.message, color: "error", timeout: 5000 });
      } finally {
        this.deletingOverrideType = null;
      }
    },
  },
};
</script>

<style lang="scss">
.gp-page {
  .rmr-page-hero__icon .v-icon {
    opacity: 1 !important;
  }

  .rmr-table-shell__eyebrow {
    font-size: $rmr-fs-sm;
    font-weight: 800;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: $rmr-text-subtle;
  }

}

.gp-table-shell__title-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.gp-table-shell__count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  background: $rmr-indigo-bg;
  color: $rmr-indigo-dark;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

.gp-card-slide-enter-active,
.gp-card-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.gp-card-slide-enter,
.gp-card-slide-leave-to {
  opacity: 0;
  transform: translateX(18px);
}

.gp-table__brand-name {
  display: inline-block;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  font-size: 13px;
  color: $rmr-text-secondary;
}

.gp-table__badge {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

.gp-table__chips {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.gp-image-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;

  &--author {
    background: $rmr-green-bg;
    color: $rmr-green-dark;
  }

  &--presentation {
    background: $rmr-sessions-soft;
    color: $rmr-sessions;
  }

  &--social {
    background: $rmr-rpm-soft;
    color: $rmr-rpm;
  }
}

.gp-table__report {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 32px;
}

.gp-table__empty {
  color: $rmr-text-subtle;
  font-size: 13px;
}

.gp-table__actions {
  min-width: 360px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;

  .rmr-action-btn {
    flex: 0 0 auto;
  }

  > * + * {
    margin-left: 6px;
  }
}

.gp-table__action-button {
  border-width: 1px !important;

  &.rmr-action-btn--transparent {
    background: white !important;
  }

  &--generate.rmr-action-btn--transparent {
    background: #fffbeb !important;
  }

  &--pdf.rmr-action-btn--transparent {
    background: #fef2f2 !important;
  }

  &--publish.rmr-action-btn--transparent {
    background: #eff6ff !important;
  }
}

.gp-publish-tooltip.v-tooltip__content {
  background: #1E88E5 !important;
  color: white !important;
  border-radius: 8px !important;
  padding: 5px 10px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 1.3 !important;
  white-space: nowrap !important;
  max-width: min(90vw, 520px);
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.4) !important;
}

.pdf-override-priority-warning {
  padding: 12px 14px;
  border-radius: 14px;
  background: $rmr-sessions-soft;
  border: 1px solid $rmr-sessions-tint;
}

.pdf-override-priority-warning__title {
  color: $rmr-sessions;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 4px;
}

.pdf-override-priority-warning__text {
  color: $rmr-text-secondary;
  font-size: 13px;
  line-height: 1.45;
}

.pdf-override-section__inputs {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.pdf-override-section__status--stack {
  gap: 6px;
}

.pdf-override-section__priority-note {
  margin-top: 10px;
  font-size: 12px;
  color: $rmr-ca-dark;
  background: $rmr-ca-soft;
  border: 1px solid $rmr-ca-tint;
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.45;
}

.gp-help__section + .gp-help__section {
  margin-top: 18px;
}

.gp-help__title {
  font-size: 13px;
  font-weight: 800;
  color: $rmr-indigo-dark;
  margin-bottom: 8px;
}

.gp-help__list {
  margin: 0;
  padding-left: 18px;
  color: $rmr-text-secondary;
  line-height: 1.6;
}

.gp-publish-tooltip--error.v-tooltip__content {
  background: #dc2626 !important;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4) !important;
}

.pdf-override-section {
  padding: 16px;
  border-radius: $rmr-radius-lg;
  border: 1px solid $rmr-border-hover;
  background: $rmr-surface-1;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &--active {
    border-color: rgba($rmr-green, 0.18);
    background: linear-gradient(135deg, $rmr-green-bg 0%, rgba($rmr-green-bg, 0.65) 100%);
    box-shadow: inset 0 0 0 1px rgba($rmr-green, 0.05);
  }

  &__label {
    font-size: 13px;
    font-weight: 700;
    color: $rmr-text-primary;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__hint {
    font-size: 12px;
    font-weight: 600;
    color: $rmr-text-subtle;
  }

  &__remove.rmr-icon-btn--delete {
    background: #dc2626 !important;
    width: 26px !important;
    height: 26px !important;

    .v-icon {
      color: white !important;
    }
  }
}

@media (max-width: 960px) {
  .gp-page {
    .gp-table-shell__title-row {
      flex-wrap: wrap;
    }
  }

  .gp-table__actions {
    min-width: 0;
    gap: 4px;

    > * + * {
      margin-left: 4px;
    }
  }
}
</style>
